/**
 * Sales Negotiation: which lines a character speaks out loud, and how.
 *
 * Pure functions with no imports and no Vite-only APIs, because TWO things load
 * this file: the Voice Room (browser) and the clip generator
 * (backend/scripts/generate-voice-clips.mjs, plain Node). Keeping the slot
 * rules in one place is what stops "the line VoiceRoom shows" and "the clip
 * that was generated" from drifting apart.
 *
 * A SLOT is the key of the line in `character.lines`, with the opening line
 * always called 'opening':
 *   opening                the line spoken when the call connects
 *   <stage.lineKey>        each line the silence profile can trigger
 *                          (silence_1, silence_2, impatient1, impatient2,
 *                          lastChance, hangup)
 */

/* Stage directions written into the line text for the on-screen transcript
   ("*click*", "[espresso machine hissing]") are not words to say aloud. */
const STAGE_DIRECTION = /\*[^*]*\*|\[[^\]]*\]/g;

export const speakableText = (raw) =>
  String(raw ?? '')
    .replace(STAGE_DIRECTION, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?;:…])/g, '$1')
    .trim();

/* The cold-call characters name their opening line `opening_a`; the other 115
   name it `opening`. Reading only `opening_a` left those 115 starting the call
   with an empty line. `opening_b` is the alternate take some cold-call
   characters carry. */
const OPENING_KEYS = ['opening_a', 'opening', 'opening_b'];

export const openingLine = (character) =>
  character?.lines?.opening_a ?? character?.lines?.opening ?? character?.lines?.opening_b ?? '';

export const lineForSlot = (character, slot) =>
  slot === 'opening' ? openingLine(character) : (character?.lines?.[slot] ?? '');

/* Every slot this character can speak, in play order, with the text to
   synthesize. Slots whose text is nothing but stage directions are dropped. */
/* `includeUnused`: also list every other written line (e.g. a silence_1 that no stage triggers, because
   the character's profile hangs up with no warning). The app never plays those; the clip generator
   asks for them to keep audio for a future reply bank. In that mode the order is opening, then the
   lines in the order the character file writes them. */
export const spokenLines = (character, { includeUnused = false } = {}) => {
  const slots = ['opening'];
  if (includeUnused) {
    const openingKey = OPENING_KEYS.find((k) => character?.lines?.[k]);   // the one the 'opening' slot already speaks
    for (const key of Object.keys(character?.lines ?? {})) {
      if (key !== openingKey && !slots.includes(key)) slots.push(key);
    }
  } else {
    for (const stage of character?.silenceProfile?.stages ?? []) {
      if (stage.lineKey && !slots.includes(stage.lineKey)) slots.push(stage.lineKey);
    }
  }
  return slots
    .map((slot) => ({ slot, raw: lineForSlot(character, slot), text: speakableText(lineForSlot(character, slot)) }))
    .filter((l) => l.text);
};

/* Short synchronous fingerprint of the text a clip was generated from (FNV-1a,
   32-bit, hex). The manifest stores it so the browser can tell that a line was
   edited after its audio was made and NOT play stale audio over new text. Not a
   security hash. */
export const textFingerprint = (text) => {
  let h = 0x811c9dc5;
  const s = String(text ?? '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};
